import { View, Text, ScrollView, TouchableOpacity } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import ShortcutCard from './ShortcutCard';
import SectionTitle from '@/components/General/SectionTitle';
import Unicon from '@/components/General/Unicon';
import ContentLoader from '@/components/General/ContentLoader';
import usePlatform from '@/utils/hooks/usePlatform';
import useShortcutAccess from '@/utils/hooks/useShortcutAccess';

const ShortcutsSection = ({ shortcuts = [], isLoading, onAddShortcut, onEditShortcut }) => {
    const { isWeb } = usePlatform();
    const canAccess = useShortcutAccess();
    // Hide saved shortcuts the user can no longer open (permission may be regained).
    const visibleShortcuts = shortcuts.filter((s) => canAccess(s.screen));

    if (isLoading) {
        return (
            <View style={styles.container}>
                <SectionTitle title="Shortcuts" marginLeft={0} marginTop={0} fontSize={12} />
                <ContentLoader />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {visibleShortcuts.length === 0 ? (
                <TouchableOpacity testID="dashboard-add-shortcut" accessibilityRole="button" accessibilityLabel="Add shortcut" style={styles.emptyState} onPress={onAddShortcut}>
                    <View style={styles.emptyIconContainer}>
                        <Unicon name="add-circle-outline" size={24} colorVariant="secondary" />
                    </View>
                    <Text style={styles.emptyText}>Add shortcuts for quick access</Text>
                </TouchableOpacity>
            ) : (
                <ScrollView 
                    horizontal 
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={styles.scrollContent(isWeb)}
                    keyboardShouldPersistTaps="handled"
                >
                    {visibleShortcuts.map((shortcut) => (
                        <ShortcutCard 
                            key={shortcut.id} 
                            shortcut={shortcut}
                            onLongPress={() => onEditShortcut?.(shortcut)}
                        />
                    ))}
                    <TouchableOpacity testID="dashboard-add-shortcut" accessibilityRole="button" accessibilityLabel="Add shortcut" style={styles.addCard} onPress={onAddShortcut}>
                        <View style={styles.addIconContainer}>
                            <Unicon name="add-outline" size={20} colorVariant="primary" />
                        </View>
                    </TouchableOpacity>
                </ScrollView>
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing }, { isPortrait }) => ({
    container: {
        paddingBottom: spacing.md,
        marginHorizontal: isPortrait ? spacing.md : spacing.lg,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
    },
    addButton: {
        padding: 4,
    },
    scrollContent: (isWeb) => ({
        paddingVertical: 4,
        paddingHorizontal: 0,
        flexWrap: 'wrap',
        width: isWeb ? "100%" : "auto",
    }),
    emptyState: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        backgroundColor: colors.surface,
        borderRadius: 12,
        marginHorizontal: 0,
    },
    emptyIconContainer: {
        marginRight: 12,
    },
    emptyText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    addCard: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
        borderRadius: 12,
        backgroundColor: colors.surface,
        width: 88,
        height: 88,
        margin: 3,
    },
    addIconContainer: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: colors.surfaceVariant,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 6,
    },
    addLabel: {
        fontSize: 10,
        fontFamily: fonts.body,
        color: colors.secondary,
    },
}));

export default ShortcutsSection;
